import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';


// Client Components
import AddClientModal from '../../../components/admin/client/AddClientModal';
import ClientInfoCard from '../../../components/admin/client/ClientInfoCard';
import ClientTabs from '../../../components/admin/client/ClientTabs';
import ClientOverview from '../../../components/admin/client/ClientOverview';
import ClientEducation from '../../../components/admin/client/ClientEducation';
import ClientExperience from '../../../components/admin/client/ClientExperience';
import ClientLanguages from '../../../components/admin/client/ClientLanguages';
import ClientFinancials from '../../../components/admin/client/ClientFinancials';
import ClientFamily from '../../../components/admin/client/ClientFamily';
import ClientDocuments from '../../../components/admin/client/ClientDocuments';

const ClientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Core Data
  const [client, setClient] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Related Data
  const [cases, setCases] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [clientInvoices, setClientInvoices] = useState([]);
  const [clientPayments, setClientPayments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [uniApps, setUniApps] = useState([]);
  const [visaApps, setVisaApps] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [leadActivities, setLeadActivities] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [dependents, setDependents] = useState([]);
  const [guardians, setGuardians] = useState([]);
  const [siblings, setSiblings] = useState([]);
  const [visaRefusals, setVisaRefusals] = useState([]);

  // UI State
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem('client_detail_tab') || 'overview'; } catch { return 'overview'; }
  });
  const [fileSearch, setFileSearch] = useState('');
  const [fileStatus, setFileStatus] = useState('');
  const [showAddClientModal, setShowAddClientModal] = useState(false);

  // Cases & Agreements filters
  const [casesSearch, setCasesSearch] = useState('');
  const [casesStatus, setCasesStatus] = useState('');
  const [casesPage, setCasesPage] = useState(1);
  const [casesPageSize, setCasesPageSize] = useState(10);
  const [agreementsSearch, setAgreementsSearch] = useState('');
  const [agreementsStatus, setAgreementsStatus] = useState('');
  const [agreementsPage, setAgreementsPage] = useState(1);
  const [agreementsPageSize, setAgreementsPageSize] = useState(10);

  // Computed Data
  const totals = useMemo(() => {
    const invoiced = (clientInvoices || []).reduce((s, inv) => s + (Number(inv.amount_due) || 0), 0);
    const paid = (clientPayments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const outstanding = Math.max(0, invoiced - paid);
    return { invoiced, paid, outstanding };
  }, [clientInvoices, clientPayments]);

  const filteredCases = useMemo(() => {
    let list = cases || [];
    if (casesStatus) list = list.filter((c) => (c.status || '').toLowerCase() === casesStatus.toLowerCase());
    if (casesSearch.trim()) {
      const q = casesSearch.trim().toLowerCase();
      list = list.filter((c) => `${c.title || ''}`.toLowerCase().includes(q));
    }
    return list;
  }, [cases, casesStatus, casesSearch]);

  const casesTotalPages = Math.max(1, Math.ceil(filteredCases.length / casesPageSize));
  const casesPaged = filteredCases.slice((casesPage - 1) * casesPageSize, casesPage * casesPageSize);

  const filteredAgreements = useMemo(() => {
    let list = agreements || [];
    if (agreementsStatus) list = list.filter((a) => (a.status || '').toLowerCase() === agreementsStatus.toLowerCase());
    if (agreementsSearch.trim()) {
      const q = agreementsSearch.trim().toLowerCase();
      list = list.filter((a) => `${a.title || ''}`.toLowerCase().includes(q));
    }
    return list;
  }, [agreements, agreementsStatus, agreementsSearch]);

  const agreementsTotalPages = Math.max(1, Math.ceil(filteredAgreements.length / agreementsPageSize));
  const agreementsPaged = filteredAgreements.slice((agreementsPage - 1) * agreementsPageSize, agreementsPage * agreementsPageSize);

  const timelineItems = useMemo(() => {
    const items = [];
    const pushItem = (data) => {
      if (!data.timestamp) return;
      items.push(data);
    };

    (submissions || []).forEach((sub) =>
      pushItem({
        id: `submission-${sub.id}`,
        type: 'submission',
        title: 'Wizard submission captured',
        description: `Status: ${sub.status || 'new'}`,
        timestamp: sub.created_at
      })
    );

    (leadActivities || []).forEach((act) =>
      pushItem({
        id: `activity-${act.id}`,
        type: 'activity',
        title: `Activity: ${act.title || act.activity_type || 'Update'}`,
        description: `Outcome: ${act.outcome || 'Pending'} • Status: ${act.status || 'open'}`,
        timestamp: act.created_at
      })
    );

    (communications || []).forEach((com) =>
      pushItem({
        id: `communication-${com.id}`,
        type: 'communication',
        title: `Communication (${com.comm_type || com.direction || 'message'})`,
        description: com.summary || com.subject || 'Conversation logged',
        timestamp: com.created_at
      })
    );

    (cases || []).forEach((ca) =>
      pushItem({
        id: `case-${ca.id}`,
        type: 'case',
        title: `Case: ${ca.title || ca.id}`,
        description: `Status: ${ca.status || 'open'} • Priority: ${ca.priority || 'medium'}`,
        timestamp: ca.updated_at || ca.created_at
      })
    );

    (agreements || []).forEach((ag) =>
      pushItem({
        id: `agreement-${ag.id}`,
        type: 'agreement',
        title: `Agreement: ${ag.title || ag.id}`,
        description: `Status: ${ag.status || 'draft'} • Value: ${ag.total_amount || 0} ${ag.currency || ''}`,
        timestamp: ag.updated_at || ag.created_at
      })
    );

    (documents || []).forEach((doc) =>
      pushItem({
        id: `document-${doc.id}`,
        type: 'document',
        title: `Document: ${doc.title || doc.id}`,
        description: `Status: ${doc.status || 'pending'}`,
        timestamp: doc.updated_at || doc.created_at
      })
    );

    (uniApps || []).forEach((app) =>
      pushItem({
        id: `uni-${app.id}`,
        type: 'uni_application',
        title: `University application: ${app.programs?.name || app.id}`,
        description: `Status: ${app.status || 'draft'} • Intake: ${app.intakes?.term || '-'}`,
        timestamp: app.updated_at || app.created_at
      })
    );

    (visaApps || []).forEach((app) =>
      pushItem({
        id: `visa-${app.id}`,
        type: 'visa_application',
        title: `Visa application: ${app.visa_products ? `${app.visa_products.country} • ${app.visa_products.visa_type || ''}` : app.id}`,
        description: `Status: ${app.status || 'draft'}`,
        timestamp: app.updated_at || app.created_at
      })
    );

    return items
      .filter((item) => !!item.timestamp)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [submissions, leadActivities, communications, cases, agreements, documents, uniApps, visaApps]);

  useEffect(() => {
    setTimeline(timelineItems);
  }, [timelineItems]);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: c, error: clientErr } = await supabase.from('clients').select('*').eq('id', id).single();
      if (clientErr) throw clientErr;
      setClient(c || null);

      const [
        { data: cs },
        { data: add },
        { data: con },
        { data: cCases },
        { data: cAgreements },
        { data: cSubs },
        { data: cUni },
        { data: cVisa },
        { data: cDocs },
        { data: cDeps },
        { data: cGuards },
        { data: cSibs },
        { data: cRefusals }
      ] = await Promise.all([
        supabase.from('client_contacts').select('*').eq('client_id', id),
        supabase.from('client_addresses').select('*').eq('client_id', id),
        supabase.from('client_consents').select('*').eq('client_id', id),
        supabase
          .from('cases')
          .select('id,title,status,priority,due_date,created_at,updated_at')
          .eq('client_id', id)
          .order('updated_at', { ascending: false }),
        supabase
          .from('agreements')
          .select('id,title,status,total_amount,currency,created_at,updated_at')
          .eq('client_id', id)
          .order('updated_at', { ascending: false }),
        supabase
          .from('wizard_submissions')
          .select('*')
          .eq('client_id', id)
          .order('created_at', { ascending: false }),
        supabase
          .from('uni_applications')
          .select('id,status,created_at,updated_at,programs(name),intakes(term)')
          .eq('client_id', id)
          .order('updated_at', { ascending: false }),
        supabase
          .from('visa_applications')
          .select('id,status,created_at,updated_at,visa_products(country,visa_type,name)')
          .eq('client_id', id)
          .order('updated_at', { ascending: false }),
        supabase
          .from('submission_documents')
          .select('id,title,status,created_at,updated_at')
          .eq('client_id', id)
          .order('created_at', { ascending: false }),
        supabase.from('client_dependents').select('*').eq('client_id', id).order('created_at', { ascending: false }),
        supabase.from('client_guardians').select('*').eq('client_id', id).order('created_at', { ascending: false }),
        supabase.from('client_siblings').select('*').eq('client_id', id).order('created_at', { ascending: false }),
        supabase.from('client_visa_refusals').select('*').eq('client_id', id).order('created_at', { ascending: false })
      ]);

      const submissionIds = (cSubs || []).map((sub) => sub.id);

      const [{ data: activityByClient }, { data: commsByClient }] = await Promise.all([
        supabase
          .from('lead_activities')
          .select('id, activity_type, title, outcome, status, created_at, submission_id')
          .eq('client_id', id)
          .order('created_at', { ascending: false }),
        supabase
          .from('communication_history')
          .select('id, comm_type, direction, subject, summary, created_at, submission_id')
          .eq('client_id', id)
          .order('created_at', { ascending: false })
      ]);

      let mergedActivities = activityByClient || [];
      let mergedCommunications = commsByClient || [];

      if (submissionIds.length > 0) {
        const [activityBySubmission, commsBySubmission] = await Promise.all([
          supabase
            .from('lead_activities')
            .select('id, activity_type, title, outcome, status, created_at, submission_id')
            .in('submission_id', submissionIds)
            .order('created_at', { ascending: false }),
          supabase
            .from('communication_history')
            .select('id, comm_type, direction, subject, summary, created_at, submission_id')
            .in('submission_id', submissionIds)
            .order('created_at', { ascending: false })
        ]);

        if (Array.isArray(activityBySubmission)) {
          const existingIds = new Set(mergedActivities.map((item) => item.id));
          mergedActivities = mergedActivities.concat(
            activityBySubmission.filter((item) => !existingIds.has(item.id))
          );
        }

        if (Array.isArray(commsBySubmission)) {
          const existingIds = new Set(mergedCommunications.map((item) => item.id));
          mergedCommunications = mergedCommunications.concat(
            commsBySubmission.filter((item) => !existingIds.has(item.id))
          );
        }
      }

      setContacts(cs || []);
      setAddresses(add || []);
      setConsents(con || []);
      setCases(cCases || []);
      setAgreements(cAgreements || []);
      setSubmissions(cSubs || []);
      setUniApps(cUni || []);
      setVisaApps(cVisa || []);
      setDocuments(cDocs || []);
      setLeadActivities(mergedActivities || []);
      setCommunications(mergedCommunications || []);
      setDependents(cDeps || []);
      setGuardians(cGuards || []);
      setSiblings(cSibs || []);
      setVisaRefusals(cRefusals || []);

      // Financials: fetch invoices/payments by agreement ids
      const agreementIds = (cAgreements || []).map((a) => a.id);
      if (agreementIds.length > 0) {
        const [{ data: invs }, { data: pays }] = await Promise.all([
          supabase
            .from('invoices')
            .select('*')
            .in('agreement_id', agreementIds)
            .order('created_at', { ascending: false }),
          supabase
            .from('payments')
            .select('*')
            .in('agreement_id', agreementIds)
            .order('paid_at', { ascending: false })
        ]);
        setClientInvoices(invs || []);
        setClientPayments(pays || []);
      } else {
        setClientInvoices([]);
        setClientPayments([]);
      }
    } catch (err) {
      console.error('Client load error', err);
      setError('Failed to load client details');
      setClient(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const handleClientAdded = (newClient) => {
    navigate(`/admin/clients/${newClient.id}`);
  };

  const badgeStyles = {
    submission: 'bg-blue-900/30 text-blue-300 border border-blue-800',
    activity: 'bg-green-900/30 text-green-300 border border-green-800',
    communication: 'bg-purple-900/30 text-purple-300 border border-purple-800',
    case: 'bg-indigo-900/30 text-indigo-300 border border-indigo-800',
    agreement: 'bg-amber-900/30 text-amber-300 border border-amber-800',
    document: 'bg-gray-700 text-gray-300 border border-gray-600',
    uni_application: 'bg-sky-900/30 text-sky-300 border border-sky-800',
    visa_application: 'bg-teal-900/30 text-teal-300 border border-teal-800'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-blue-400 font-mono animate-pulse">LOADING CLIENT DATA...</div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-8 text-white">Client not found.</div>
    );
  }

  return (

    <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
      {/* Header */}
      <div className="mb-8 flex items-end justify-between border-b border-gray-800 pb-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight uppercase">Client Details</h1>
          <p className="text-gray-500 text-sm mt-1 font-mono">Comprehensive client management and activity overview</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 rounded text-xs font-bold bg-gray-700 text-white hover:bg-gray-600 uppercase border border-gray-600">
            ← Back
          </button>
          <button onClick={() => setShowAddClientModal(true)} className="px-4 py-2 rounded text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 uppercase border border-blue-500 shadow-lg shadow-blue-900/20">
            + Add New Client
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 rounded border mb-6 bg-red-900/20 border-red-800 text-red-400">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto space-y-6">
        {/* Client Info Card */}
        <ClientInfoCard
          client={client}
          onAddCase={() => navigate(`/admin/cases/new?clientId=${id}`)}
          onAddAgreement={() => navigate(`/admin/agreements/builder?clientId=${id}`)}
        />

        <ClientTabs
          selectedTab={tab}
          setSelectedTab={setTab}
          counts={{
            cases: cases.length,
            agreements: agreements.length,
            documents: documents.length,
            invoices: clientInvoices.length
          }}
        />

        <div className="animate-fadeIn">
          {tab === 'overview' && (
            <ClientOverview
              client={client}
              contacts={contacts}
              addresses={addresses}
              consents={consents}
              submissions={submissions}
              onRefresh={load}
            />
          )}

          {tab === 'education' && (
            <ClientEducation client={client} submissions={submissions} onRefresh={load} />
          )}

          {tab === 'experience' && (
            <ClientExperience submissions={submissions} onRefresh={load} />
          )}

          {tab === 'languages' && (
            <ClientLanguages client={client} submissions={submissions} onRefresh={load} />
          )}

          {tab === 'family' && (
            <ClientFamily
              clientId={id}
              dependents={dependents}
              guardians={guardians}
              siblings={siblings}
              visaRefusals={visaRefusals}
              onRefresh={load}
            />
          )}

          {tab === 'financials' && (
            <ClientFinancials
              submissions={submissions}
              invoices={clientInvoices}
              payments={clientPayments}
              totals={totals}
              onRefresh={load}
            />
          )}

          {tab === 'documents' && (
            <ClientDocuments
              documents={documents}
              submissions={submissions}
              fileSearch={fileSearch}
              setFileSearch={setFileSearch}
              fileStatus={fileStatus}
              setFileStatus={setFileStatus}
            />
          )}

          {tab === 'cases' && (
            <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
              <div className="bg-gray-900/50 p-4 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-bold text-white uppercase tracking-wider">🗂️ Cases</h3>
                <Link to="/admin/cases" className="px-3 py-1.5 rounded text-xs font-bold bg-gray-700 text-white hover:bg-gray-600 uppercase border border-gray-600">
                  View All →
                </Link>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <input
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                    placeholder="🔍 Search cases"
                    value={casesSearch}
                    onChange={(e) => { setCasesSearch(e.target.value); setCasesPage(1); }}
                  />
                  <select
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
                    value={casesStatus}
                    onChange={(e) => { setCasesStatus(e.target.value); setCasesPage(1); }}
                  >
                    <option value="">All statuses</option>
                    {['pending', 'active', 'done', 'closed'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
                    value={casesPageSize}
                    onChange={(e) => { setCasesPageSize(parseInt(e.target.value) || 10); setCasesPage(1); }}
                  >
                    {[10, 20, 50].map(n => <option key={n} value={n}>{n}/page</option>)}
                  </select>
                </div>
                <ul className="space-y-3">
                  {casesPaged.map((ca) => (
                    <li key={ca.id} className="p-4 bg-gray-700/50 rounded border border-gray-600 hover:bg-gray-700 transition-colors">
                      <div className="flex items-center justify-between">
                        <Link to={`/admin/cases/${ca.id}`} className="font-semibold text-white hover:text-blue-400">
                          {ca.title || ca.id}
                        </Link>
                      </div>
                      <div className="text-xs text-gray-400 mt-2 flex items-center gap-3 flex-wrap">
                        <span className={`px-2 py-1 rounded ${ca.status === 'active' ? 'bg-green-900/30 text-green-300 border border-green-800' : 'bg-gray-700 text-gray-400 border border-gray-600'}`}>
                          {ca.status}
                        </span>
                        <span>Priority: {ca.priority}</span>
                        <span>Updated: {ca.updated_at ? new Date(ca.updated_at).toLocaleDateString() : '-'}</span>
                      </div>
                    </li>
                  ))}
                  {casesPaged.length === 0 && (
                    <li className="text-sm text-gray-400 text-center py-12">No cases found</li>
                  )}
                </ul>
                {filteredCases.length > casesPageSize && (
                  <div className="mt-4 flex items-center justify-between text-sm text-gray-400">
                    <div>Showing {(casesPage - 1) * casesPageSize + 1}-{Math.min(casesPage * casesPageSize, filteredCases.length)} of {filteredCases.length}</div>
                    <div className="flex items-center gap-2">
                      <button disabled={casesPage <= 1} onClick={() => setCasesPage(1)} className="px-3 py-1.5 rounded text-xs font-bold bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-50 border border-gray-600">First</button>
                      <button disabled={casesPage <= 1} onClick={() => setCasesPage(p => Math.max(1, p - 1))} className="px-3 py-1.5 rounded text-xs font-bold bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-50 border border-gray-600">Prev</button>
                      <span>Page {casesPage} / {casesTotalPages}</span>
                      <button disabled={casesPage >= casesTotalPages} onClick={() => setCasesPage(p => Math.min(casesTotalPages, p + 1))} className="px-3 py-1.5 rounded text-xs font-bold bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-50 border border-gray-600">Next</button>
                      <button disabled={casesPage >= casesTotalPages} onClick={() => setCasesPage(casesTotalPages)} className="px-3 py-1.5 rounded text-xs font-bold bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-50 border border-gray-600">Last</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === 'agreements' && (
            <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
              <div className="bg-gray-900/50 p-4 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-bold text-white uppercase tracking-wider">📑 Agreements</h3>
                <Link to="/admin/agreements" className="px-3 py-1.5 rounded text-xs font-bold bg-gray-700 text-white hover:bg-gray-600 uppercase border border-gray-600">
                  View All →
                </Link>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <input
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                    placeholder="🔍 Search agreements"
                    value={agreementsSearch}
                    onChange={(e) => { setAgreementsSearch(e.target.value); setAgreementsPage(1); }}
                  />
                  <select
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
                    value={agreementsStatus}
                    onChange={(e) => { setAgreementsStatus(e.target.value); setAgreementsPage(1); }}
                  >
                    <option value="">All statuses</option>
                    {['draft', 'sent', 'accepted', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
                    value={agreementsPageSize}
                    onChange={(e) => { setAgreementsPageSize(parseInt(e.target.value) || 10); setAgreementsPage(1); }}
                  >
                    {[10, 20, 50].map(n => <option key={n} value={n}>{n}/page</option>)}
                  </select>
                </div>
                <ul className="space-y-3">
                  {agreementsPaged.map((ag) => (
                    <li key={ag.id} className="p-4 bg-gray-700/50 rounded border border-gray-600 hover:bg-gray-700 transition-colors">
                      <div className="flex items-center justify-between">
                        <Link to={`/admin/agreements/${ag.id}`} className="font-semibold text-white hover:text-blue-400">
                          {ag.title || ag.id}
                        </Link>
                      </div>
                      <div className="text-xs text-gray-400 mt-2 flex items-center gap-3 flex-wrap">
                        <span className={`px-2 py-1 rounded ${ag.status === 'accepted' ? 'bg-green-900/30 text-green-300 border border-green-800' : 'bg-gray-700 text-gray-400 border border-gray-600'}`}>
                          {ag.status || 'draft'}
                        </span>
                        <span>Value: {ag.total_amount || 0} {ag.currency || ''}</span>
                      </div>
                    </li>
                  ))}
                  {agreementsPaged.length === 0 && (
                    <li className="text-sm text-gray-400 text-center py-12">No agreements found</li>
                  )}
                </ul>
                {filteredAgreements.length > agreementsPageSize && (
                  <div className="mt-4 flex items-center justify-between text-sm text-gray-400">
                    <div>Showing {(agreementsPage - 1) * agreementsPageSize + 1}-{Math.min(agreementsPage * agreementsPageSize, filteredAgreements.length)} of {filteredAgreements.length}</div>
                    <div className="flex items-center gap-2">
                      <button disabled={agreementsPage <= 1} onClick={() => setAgreementsPage(1)} className="px-3 py-1.5 rounded text-xs font-bold bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-50 border border-gray-600">First</button>
                      <button disabled={agreementsPage <= 1} onClick={() => setAgreementsPage(p => Math.max(1, p - 1))} className="px-3 py-1.5 rounded text-xs font-bold bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-50 border border-gray-600">Prev</button>
                      <span>Page {agreementsPage} / {agreementsTotalPages}</span>
                      <button disabled={agreementsPage >= agreementsTotalPages} onClick={() => setAgreementsPage(p => Math.min(agreementsTotalPages, p + 1))} className="px-3 py-1.5 rounded text-xs font-bold bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-50 border border-gray-600">Next</button>
                      <button disabled={agreementsPage >= agreementsTotalPages} onClick={() => setAgreementsPage(agreementsTotalPages)} className="px-3 py-1.5 rounded text-xs font-bold bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-50 border border-gray-600">Last</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === 'timeline' && (
            <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
              <div className="bg-gray-900/50 p-4 border-b border-gray-700">
                <h3 className="text-lg font-bold text-white uppercase tracking-wider">⏱️ Activity Timeline</h3>
              </div>
              <div className="p-6">
                {timeline.length === 0 ? (
                  <div className="text-sm text-gray-400 text-center py-16">📭 No activity recorded yet.</div>
                ) : (
                  <div className="space-y-3">
                    {timeline.map((item) => (
                      <div key={item.id} className="p-4 bg-gray-700/50 rounded border border-gray-600">
                        <div className="flex items-start gap-3">
                          <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${badgeStyles[item.type] || 'bg-gray-700 text-gray-400'}`}>
                            {item.type.replace('_', ' ')}
                          </span>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div className="font-semibold text-white">{item.title}</div>
                              <div className="text-xs text-gray-500">
                                {new Date(item.timestamp).toLocaleString()}
                              </div>
                            </div>
                            {item.description && (
                              <div className="text-sm text-gray-400 mt-1">{item.description}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Client Modal */}
      {showAddClientModal && (
        <AddClientModal
          onClose={() => setShowAddClientModal(false)}
          onClientAdded={handleClientAdded}
        />
      )}
    </div>

  );
};

export default ClientDetailPage;
